import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { PortalAuthService } from '../../core/auth/portal-auth.service';
import { SeoService } from '../../core/services/seo.service';
import { PortalNavbarComponent } from '../../core/components/portal-navbar/portal-navbar.component';
import { IonicModule } from '@ionic/angular';
import { PortalFooterComponent } from '../../core/components/portal-footer/portal-footer.component';
import { TranslatePipe } from '@ngx-translate/core';

interface SecurityItem {
  icon: string;
  titleKey: string;
  descKey: string;
}

// Only safeguards that exist in the code or the docs: AuthMiddleware (Auth0 JWT), roles and
// permissions, row-level tenant isolation, HSTS (server.js/web.config), IdleTimeoutService
// (30 min) and the append-only AuditLog. Do not add a claim here without a source.
const MEASURES: SecurityItem[] = [
  { icon: 'log-in-outline', titleKey: 'security.measure.auth.title', descKey: 'security.measure.auth.desc' },
  { icon: 'key-outline', titleKey: 'security.measure.roles.title', descKey: 'security.measure.roles.desc' },
  { icon: 'business-outline', titleKey: 'security.measure.tenants.title', descKey: 'security.measure.tenants.desc' },
  { icon: 'lock-closed-outline', titleKey: 'security.measure.transport.title', descKey: 'security.measure.transport.desc' },
  { icon: 'timer-outline', titleKey: 'security.measure.idle.title', descKey: 'security.measure.idle.desc' },
  { icon: 'reader-outline', titleKey: 'security.measure.audit.title', descKey: 'security.measure.audit.desc' },
];

const GUARDIAN_POINTS: SecurityItem[] = [
  { icon: 'phone-portrait-outline', titleKey: 'security.guardian.point1.title', descKey: 'security.guardian.point1.desc' },
  { icon: 'notifications-outline', titleKey: 'security.guardian.point2.title', descKey: 'security.guardian.point2.desc' },
  { icon: 'settings-outline', titleKey: 'security.guardian.point3.title', descKey: 'security.guardian.point3.desc' },
];

const TIPS: SecurityItem[] = [
  { icon: 'finger-print-outline', titleKey: 'security.tip.password.title', descKey: 'security.tip.password.desc' },
  { icon: 'shield-checkmark-outline', titleKey: 'security.tip.mfa.title', descKey: 'security.tip.mfa.desc' },
  { icon: 'log-out-outline', titleKey: 'security.tip.logout.title', descKey: 'security.tip.logout.desc' },
  { icon: 'people-outline', titleKey: 'security.tip.access.title', descKey: 'security.tip.access.desc' },
  { icon: 'eye-off-outline', titleKey: 'security.tip.secrets.title', descKey: 'security.tip.secrets.desc' },
];

@Component({
    selector: 'app-security',
    templateUrl: './security.page.html',
    styleUrls: ['./security.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        PortalNavbarComponent,
        IonicModule,
        RouterLink,
        PortalFooterComponent,
        TranslatePipe,
    ],
})
export class SecurityPage implements OnInit, OnDestroy {
  readonly measures = MEASURES;
  readonly guardianPoints = GUARDIAN_POINTS;
  readonly tips = TIPS;

  /** Drives the header variant: app-portal-navbar while signed out; signed in, the
   *  app shell's own header takes over instead (see AppComponent.hideAppShell$). */
  isAuthenticated = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly seo: SeoService,
    private readonly portalAuth: PortalAuthService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.seo.apply({ titleKey: 'security.seoTitle', descriptionKey: 'security.heroSub', path: '/security' });

    this.portalAuth.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe(auth => {
      this.isAuthenticated = auth;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
